/**
 * The assistant types, re-exported from the generated SDK.
 *
 * This file used to hand-mirror §1–§7 of the API contract because the backend
 * routes did not exist yet and there was nothing to generate from. They exist
 * now, so every shape below comes from `openapi/openapi.json` via
 * `pnpm run generate-client`: the frontend cannot drift from the server it
 * actually talks to, and a backend field change shows up as a `tsc` error
 * instead of `undefined` at runtime.
 *
 * What remains local, and why:
 *
 *   - `ApiErrorResponse` is an alias for the generated `ErrorResponse`, the one
 *     error envelope §1 defines. The old name is kept so the components reading
 *     `message.error` did not all have to change.
 *   - `AssistantStreamState` aliases the generated `AssistantStatusState`.
 *   - `ActorKind` is the four-value union the contract specifies. The backend
 *     currently types `ActorRef.kind` as a bare `str`, so the generated model
 *     cannot narrow it — see the follow-up note in the integration report.
 *   - The display-order constant array and `asKnowledgeSourceSystem()` are
 *     runtime values, not shapes, and have no generated equivalent.
 *
 * Note how many fields the generated models mark optional: the backend gives
 * them Pydantic defaults, so the wire may legitimately omit them. Treat
 * `citations`, `ticket_refs`, `items` and friends as
 * possibly `undefined` rather than trusting the contract's prose.
 */

import {
  type AssistantRequestFormPrefill,
  type AssistantStatusState,
  type ErrorResponse,
  type KnowledgeSourceSystem,
  type OfferingParameter,
  type TicketOfferingAnswer,
  type TicketRef,
} from "@/lib/api";

export type {
  ActorRef,
  AssistantAccessCardEvent,
  AssistantCitation,
  AssistantCitationEvent,
  AssistantContext,
  AssistantConversationCreateRequest,
  AssistantConversationDetail,
  AssistantConversationListResponse,
  AssistantConversationSummary,
  AssistantDoneEvent,
  AssistantErrorEvent,
  AssistantFinishReason,
  AssistantMessage,
  AssistantMessageAcceptedResponse,
  AssistantMessageCreateRequest,
  AssistantMessageListResponse,
  AssistantMessageStatus,
  AssistantRequestFormPrefill,
  AssistantRole,
  AssistantStatusEvent,
  AssistantStreamEvent,
  AssistantStreamEventType,
  AssistantTextEvent,
  AssistantTicketRefEvent,
  DeletionResponse,
  KnowledgeSourceSystem,
  LocalizedString,
  OfferingParameter,
  OfferingParameterDependency,
  OfferingParameterOption,
  OfferingParameterType,
  PageInfo,
  TicketKind,
  TicketOfferingAnswer,
  TicketRef,
  TicketStatusCode,
  TranslatableText,
} from "@/lib/api";

/** §1 `ErrorResponse` — the single error envelope for every non-2xx. */
export type ApiErrorResponse = ErrorResponse;

/** The states the "searching knowledge…" chip can show (§4.2). */
export type AssistantStreamState = AssistantStatusState;

/**
 * §1 `ActorRef.kind`. The generated model types it as `string` because the
 * backend does, so this union stays local until the backend narrows it.
 */
export type ActorKind = "USER" | "AGENT" | "SYSTEM" | "ASSISTANT";

/** Every knowledge source the filter offers, in display order. */
export const KNOWLEDGE_SOURCE_SYSTEMS = [
  "HELPDESK",
  "SHAREPOINT",
  "CLICKLEARN",
  "MAILBOX",
  "ITSM",
  "UNKNOWN",
] as const satisfies readonly KnowledgeSourceSystem[];

/**
 * `UNKNOWN` is the mandated fallback for every enum in the contract: clients
 * must tolerate a value the server added after this build shipped rather than
 * dropping the row.
 */
export function asKnowledgeSourceSystem(value: string): KnowledgeSourceSystem {
  return (KNOWLEDGE_SOURCE_SYSTEMS as readonly string[]).includes(value)
    ? (value as KnowledgeSourceSystem)
    : "UNKNOWN";
}

// ─── Inline request forms (§4.2 `request_form`) ──────────────────────────
//
// PENDING BACKEND. The assistant now streams the *whole* form for a catalog
// offering instead of interrogating the user field by field, and the browser
// files the answers itself. Neither the event nor the two endpoints behind it
// are in `openapi/openapi.json` yet, so the shapes below are hand-written
// against the agreed contract and must be deleted the moment
// `pnpm run generate-client` produces them. Everything that touches the wire is
// confined to this block and to the two wrappers in `./api`, so the swap is a
// two-file change and `tsc` will point at every call site that drifted.

/**
 * `request_form` — "here is the form; fill it in".
 *
 * `parameters` is deliberately the same `OfferingParameter` the catalog
 * endpoints already return, which is what lets the transcript reuse the
 * portal's dynamic field renderer rather than growing a second one.
 */
export interface AssistantRequestFormEvent {
  type: "request_form";
  offering_id: string;
  offering_name: string;
  description: string | null;
  requires_approval: boolean;
  /** Working days the catalog quotes for fulfilment, when it quotes one. */
  delivery_days: number | null;
  parameters: OfferingParameter[];
  /**
   * Initial values from what the person said in the chat, already validated
   * server-side against `parameters`. Optional: an older server omits it and
   * the form opens blank, as it always did.
   */
  prefill?: AssistantRequestFormPrefill[];
}

/**
 * `incident_form` — "here is the incident form; check it and send it".
 *
 * Every field is a **prefill**, not a definition. The assistant has already
 * been talking to the person, so it hands over their description (`details`),
 * the urgency when it was clear, and any follow-up questions it already asked
 * with their answers (at most three). `null` / empty means it did not know.
 *
 * There is no `offering_id` to key a card on, because an incident is not a
 * catalog item. The card is identified by the message that carried it plus its
 * position in that turn.
 */
export interface AssistantIncidentFormEvent {
  type: "incident_form";
  subject: string | null;
  details: string | null;
  urgency_code: "LOW" | "MEDIUM" | "HIGH" | null;
  followups: { question: string; answer: string }[];
  /**
   * The colleague it is being reported for, when the person said so and the
   * assistant found them in the directory. Absent from older servers; null
   * (the normal case) means it is for the signed-in person.
   */
  on_behalf_of?: { email: string; display_name: string } | null;
}

/**
 * One field the server refused.
 *
 * `message` is written for the model, not for a person — it says things like
 * "Ask the user for it" — so the card renders a translated string keyed on
 * `code` and keeps `message` only for the log.
 */
export interface ServiceRequestFieldError {
  parameter_id: string;
  name?: string | null;
  code: string;
  message?: string | null;
}

/** The body of `prepare_service_request`. Validates and holds; writes nothing. */
export interface ServiceRequestPrepareRequest {
  conversation_id?: string | null;
  offering_id: string;
  subject: string;
  description: string;
  body_lang: string;
  answers: TicketOfferingAnswer[];
  /**
   * Files attached in the chat (not to an ATTACHMENT question) that go with
   * the request. The server carries only the caller's own uploads.
   */
  chat_attachment_ids?: string[];
}

/** What `prepare_service_request` hands back: a draft, or the fields to fix. */
export interface ServiceRequestDraft {
  draft_id?: string | null;
  offering_id?: string | null;
  offering_name?: string | null;
  subject?: string | null;
  description?: string | null;
  requires_approval?: boolean;
  field_errors?: ServiceRequestFieldError[];
}

/**
 * The body of `submit_service_request`.
 *
 * `user_confirmed` is the same gate the tool enforces: the server files nothing
 * unless a person pressed the button that sets it.
 */
export interface ServiceRequestSubmitRequest {
  draft_id: string;
  user_confirmed: boolean;
  conversation_id?: string | null;
}

/** The filed request. Normalised in `./api` from whichever envelope arrives. */
export interface ServiceRequestSubmission {
  ticket: TicketRef;
  /**
   * Answers the request was filed without.
   *
   * Parameters are written as separate records after the request exists, so
   * some can fail while the request itself succeeds. Reporting an unqualified
   * success over a half-empty ticket is the failure mode that let every
   * request go out empty for weeks without anyone noticing.
   */
  unattached_answers?: string[];
  /**
   * Files that should be on the request and are not (expired, refused, or the
   * upload to Ivanti failed). The request is filed anyway; the card says so.
   */
  unattached_files?: UnattachedFile[];
}

/** A file that did not reach the request. `filename` is null when the server no longer holds it. */
export interface UnattachedFile {
  attachment_id: string;
  filename?: string | null;
}
